import type { TshirtSize } from "@/db/schema";

/** Display order for t-shirt sizes, smallest to largest. */
export const TSHIRT_SIZE_ORDER: TshirtSize[] = [
  "xs",
  "sm",
  "m",
  "l",
  "xl",
  "xxl",
];

/** Pre-built options list for Select/ComboBox components. */
export const TSHIRT_SIZE_OPTIONS = TSHIRT_SIZE_ORDER.map((s) => ({
  value: s,
  display: s.toUpperCase(),
}));

/**
 * Count, by size, the shirts that still need to be handed out: items that have
 * a size selected and have NOT been picked up yet (`tshirtPickedUpAt == null`).
 * Backs the "Shirts needed" totals shown on screen and in the printed bundle —
 * already-picked-up shirts are excluded so the count reflects real remaining need.
 *
 * Returns `[size, count]` entries ordered by {@link TSHIRT_SIZE_ORDER}, so
 * callers can render directly without re-sorting (sizes with a zero count are
 * omitted).
 */
export function countNeededShirtSizes(
  items: Iterable<{ tshirtSize: string | null; tshirtPickedUpAt: Date | null }>,
): [TshirtSize, number][] {
  const counts = new Map<string, number>();
  for (const item of items) {
    if (!item.tshirtSize || item.tshirtPickedUpAt != null) continue;
    counts.set(item.tshirtSize, (counts.get(item.tshirtSize) ?? 0) + 1);
  }
  return TSHIRT_SIZE_ORDER.filter((size) => counts.has(size)).map((size) => [
    size,
    counts.get(size)!,
  ]);
}
